import React, { useState, useEffect } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { TENANTS } from '../../config/Config';
import { useNavigate } from 'react-router-dom';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps } from '../../utils/tableUtils';
import api from '../../services/api';

export default function TenantManagementPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [filters, setFilters] = useState({ domain: '', name: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ domain: '', name: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(TENANTS);
      const items = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setTenants(items);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      showSnackbar('Failed to load tenants', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tenant?')) return;
    try {
      await api.delete(`${TENANTS}${id}/`);
      showSnackbar('Tenant deleted successfully');
      fetchData();
    } catch (err) {
      console.error('Error deleting tenant:', err);
      showSnackbar('Delete failed', 'error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpen = (tenant = null) => {
    if (tenant) {
      setForm({ domain: tenant.domain, name: tenant.name });
      setEditMode(true);
      setCurrentId(tenant.id);
    } else {
      setForm({ domain: '', name: '' });
      setEditMode(false);
      setCurrentId(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ domain: '', name: '' });
    setEditMode(false);
    setCurrentId(null);
  };

  const handleSubmit = async () => {
    if (!form.domain || !form.name) {
      showSnackbar('Please fill in all fields', 'error');
      return;
    }

    try {
      if (editMode) {
        await api.patch(`${TENANTS}${currentId}/`, form);
        showSnackbar('Tenant updated successfully');
      } else {
        await api.post(TENANTS, form);
        showSnackbar('Tenant created successfully');
      }
      handleClose();
      fetchData();
    } catch (err) {
      console.error('Error saving tenant:', err);
      showSnackbar('Operation failed', 'error');
    }
  };

  const filteredTenants = filterData(tenants, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { domain: '', name: '' });

  const filterConfig = [
    {
      field: 'domain',
      label: 'Domain',
      type: 'text',
      placeholder: 'Search by domain...'
    },
    {
      field: 'name',
      label: 'Name',
      type: 'text',
      placeholder: 'Search by name...'
    }
  ];

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      minWidth: 80
    },
    {
      field: 'domain',
      headerName: 'Domain',
      flex: 2,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 2
    },
    {
      field: 'created_at',
      headerName: 'Created',
      flex: 1,
      valueGetter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ p: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ p: 0.5, color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const actionButtons = [
    {
      label: 'Add Tenant',
      icon: <AddIcon />,
      variant: 'contained',
      onClick: () => handleOpen(null)
    }
  ];

  const emptyStateProps = {
    title: 'No tenants found',
    description: 'Try adjusting your filters or add your first tenant.',
    icon: <AddIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Tenants"
        actionButtons={actionButtons}
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredTenants.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <DataGrid
          rows={filteredTenants}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Tenant' : 'Add Tenant'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Domain"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
            fullWidth
            margin="normal"
            autoFocus
            placeholder="e.g., example.com"
          />
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
            placeholder="e.g., Example Organization"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
